import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import Button from 'react-bootstrap/Button';
import Alert from 'react-bootstrap/Alert';
import { db, functions } from '../firebase.js';
import { useAuth } from '../contexts/AuthContext.js';

export default function Challenge() {
  const { challengeId } = useParams();
  const navigate = useNavigate();
  const { currentUser, authLoaded } = useAuth();
  const [challenge, setChallenge] = useState(undefined);
  const [fromPlayer, setFromPlayer] = useState(null);
  const [error, setError] = useState(null);
  const [responding, setResponding] = useState(null);

  useEffect(() => {
    if (!currentUser) return;

    getDoc(doc(db, 'challenges', challengeId)).then(async snap => {
      if (!snap.exists()) {
        setChallenge(null);
        return;
      }
      const data = { id: snap.id, ...snap.data() };
      setChallenge(data);

      const playerSnap = await getDoc(doc(db, 'players', data.fromPlayerId));
      setFromPlayer(playerSnap.exists() ? { id: playerSnap.id, ...playerSnap.data() } : null);
    });
  }, [currentUser, challengeId]);

  async function handleAccept() {
    setResponding('accept');
    setError(null);
    try {
      const acceptChallenge = httpsCallable(functions, 'acceptChallenge');
      const result = await acceptChallenge({ challengeId });
      navigate(`/online/${result.data.onlineGameId}`);
    } catch (err) {
      setError(err.message);
      setResponding(null);
    }
  }

  async function handleDecline() {
    setResponding('decline');
    setError(null);
    try {
      const declineChallenge = httpsCallable(functions, 'declineChallenge');
      await declineChallenge({ challengeId });
      setChallenge({ ...challenge, status: 'declined' });
    } catch (err) {
      setError(err.message);
    } finally {
      setResponding(null);
    }
  }

  if (authLoaded && !currentUser) {
    return (
      <div className="center-page">
        <div className="panel" style={{ maxWidth: 400 }}>
          <h2>Challenge</h2>
          <p>Log in to view and accept this challenge.</p>
          <Link to="/login" className="pill-button">Login</Link>
        </div>
      </div>
    );
  }

  if (challenge === undefined) return <p>Loading…</p>;

  return (
    <div className="center-page">
      <div className="panel" style={{ maxWidth: 400 }}>
        <h2>Challenge</h2>
        {error && <Alert variant="danger">{error}</Alert>}

        {!challenge && <p>This challenge doesn't exist.</p>}

        {challenge && challenge.status === 'pending' && (
          <>
            <p>{fromPlayer?.name ?? 'Someone'} has challenged you to a game!</p>
            <Button variant="success" className="me-2" disabled={responding !== null} onClick={handleAccept}>
              {responding === 'accept' ? 'Accepting…' : 'Accept'}
            </Button>
            <Button variant="warning" disabled={responding !== null} onClick={handleDecline}>
              {responding === 'decline' ? 'Declining…' : 'Decline'}
            </Button>
          </>
        )}

        {challenge && challenge.status === 'accepted' && (
          <p>
            This challenge was already accepted.{' '}
            <Link to={`/online/${challenge.onlineGameId}`}>Go to game</Link>
          </p>
        )}

        {challenge && challenge.status === 'declined' && <p>This challenge was declined.</p>}
      </div>
    </div>
  );
}
